import { useRef, useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "@/lib/router-compat";
import { z } from "zod";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import GridBackdrop from "@/components/GridBackdrop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Check, FileText, Clock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { trackSubmission } from "@/lib/analytics";

const projectTypes = [
  "Marketing Website",
  "E-commerce",
  "Portfolio",
  "Landing Page",
  "Web Application",
  "Migration from WordPress/Wix",
  "Other",
];

const budgetRanges = [
  "$1,000 - $3,000",
  "$3,000 - $5,000",
  "$5,000 - $10,000",
  "$10,000 - $20,000",
  "$20,000+",
  "Not sure yet",
];

const timelines = [
  "ASAP",
  "Within 2 weeks",
  "Within 1 month",
  "Within 3 months",
  "Flexible",
];

const pageRanges = [
  "1-5 pages",
  "6-10 pages",
  "11-20 pages",
  "20+ pages",
  "Not sure",
];
const features = [
  { id: "cms", label: "CMS" },
  { id: "blog", label: "Blog" },
  { id: "ecommerce", label: "E-commerce" },
  { id: "booking", label: "Booking" },
  { id: "multilingual", label: "Multilingual" },
  { id: "memberLogin", label: "Member login" },
];

const maintenancePlans = [
  { id: "not-sure", label: "Not sure yet" },
  { id: "essential", label: "Essential - $197/month" },
  { id: "professional", label: "Professional - $347/month (Most Popular)" },
  { id: "premium", label: "Premium - $597/month" },
];

const quoteSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, { message: "Please enter your name." })
    .max(100, { message: "Name must be less than 100 characters." }),
  email: z
    .string()
    .trim()
    .email({ message: "Please enter a valid email address." })
    .max(255, { message: "Email must be less than 255 characters." }),
  phone: z
    .string()
    .trim()
    .max(30, { message: "Phone must be less than 30 characters." })
    .optional()
    .or(z.literal("")),
  company: z
    .string()
    .trim()
    .max(100, { message: "Company must be less than 100 characters." })
    .optional()
    .or(z.literal("")),
  website: z
    .string()
    .trim()
    .max(255, { message: "Website must be less than 255 characters." })
    .optional()
    .or(z.literal("")),
  projectType: z.string().min(1, { message: "Please select a project type." }),
  budget: z.string().min(1, { message: "Please select a budget range." }),
  timeline: z.string().min(1, { message: "Please select a timeline." }),
  pages: z.string().optional().or(z.literal("")),
  projectDetails: z
    .string()
    .trim()
    .min(10, { message: "Please tell us more about your project (10+ characters)." })
    .max(2000, { message: "Project details must be less than 2000 characters." }),
});

const Quote = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionPending = useRef(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    website: "",
    projectType: "",
    budget: "",
    timeline: "",
    pages: "",
    selectedFeatures: [] as string[],
    interestedInMaintenance: false,
    maintenancePlan: "",
    projectDetails: "",
    honeypot: "",
  });

  // Read plan from URL params and pre-select
  useEffect(() => {
    const plan = searchParams.get("plan");
    if (plan && maintenancePlans.some((p) => p.id === plan)) {
      setFormData((prev) => ({
        ...prev,
        interestedInMaintenance: true,
        maintenancePlan: plan,
      }));
    }
  }, [searchParams]);

  const handleFeatureToggle = (featureId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedFeatures: prev.selectedFeatures.includes(featureId)
        ? prev.selectedFeatures.filter((id) => id !== featureId)
        : [...prev.selectedFeatures, featureId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submissionPending.current) return;
    setIsSubmitting(true);
    setFormError(null);
    setFieldErrors({});

    const parsed = quoteSchema.safeParse({
      fullName: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      company: formData.company,
      website: formData.website,
      projectType: formData.projectType,
      budget: formData.budget,
      timeline: formData.timeline,
      pages: formData.pages,
      projectDetails: formData.projectDetails,
    });

    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !errs[key]) errs[key] = issue.message;
      }
      setFieldErrors(errs);
      setFormError("Please fix the highlighted fields and try again.");
      setIsSubmitting(false);
      return;
    }

    submissionPending.current = true;
    try {
      const featureLabels = formData.selectedFeatures.map(
        (id) => features.find((f) => f.id === id)?.label || id
      );

      const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
          type: "quote",
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          website: formData.website,
          projectType: formData.projectType,
          budget: formData.budget,
          timeline: formData.timeline,
          pages: formData.pages,
          features: featureLabels,
          projectDetails: formData.projectDetails,
          honeypot: formData.honeypot,
        },
      });

      if (error) throw error;
      if (data?.success !== true) throw new Error("Submission was not accepted");

      trackSubmission("quote", data);
      navigate("/thank-you/quote");
    } catch (error) {
      console.error("Error sending email:", error);
      const errorMessage = "Please try again or call us directly.";
      setFormError(errorMessage);
      toast({
        title: "Something went wrong",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      submissionPending.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title="Request a Quote"
        description="Get a custom quote for your website project from NETIUS. Based in Central Florida, we build high-performance websites for clients across the US and globally."
        path="/quote"
      />
      <Header />
      
      <main className="flex-1">
        {/* Aria-live region for form errors */}
        <div aria-live="polite" className="sr-only">
          {formError && `Error: ${formError}`}
        </div>

        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <GridBackdrop />
          <div className="container relative z-10 px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
                Request a Quote
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Share a few details and we'll follow up quickly with a clear plan and estimate. 
                Prefer to talk now? <a href="/meeting" className="text-foreground underline">Request a meeting</a>.
              </p>
              <div className="flex flex-wrap gap-x-8 gap-y-3 justify-center text-sm text-muted-foreground mt-8">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  <span>Free estimate</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  <span>No commitment</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  <span>Response within 1 business day</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quote Form */}
        <section className="py-16 border-t border-border">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* What to Expect */}
              <div className="bg-secondary/50 border border-border ring-1 ring-white/[0.08] dark:ring-white/[0.12] rounded-lg p-6 h-fit">
                <h2 className="text-xl font-bold mb-6">What to expect</h2>
                <ul className="space-y-4 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    Fast response with clarifying questions if needed
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    Clear scope outline and recommendations
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    Estimate aligned to your budget and timeline
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    No pressure. No jargon. Just a plan.
                  </li>
                </ul>
                <div className="mt-8 pt-6 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-2">Prefer email?</p>
                  <a href="mailto:web@netius.com" className="text-foreground underline text-sm">
                    web@netius.com
                  </a>
                </div>
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground mb-2">Or call us:</p>
                  <a href="tel:323-638-4871" className="text-foreground underline text-sm">
                    323-NETIUS1 (638-4871)
                  </a>
                </div>
              </div>

              {/* Form */}
              <div className="lg:col-span-2 bg-secondary/50 border border-border ring-1 ring-white/[0.08] dark:ring-white/[0.12] rounded-lg p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input
                        id="fullName"
                        placeholder="Jane Doe"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        required
                        disabled={isSubmitting}
                        aria-required="true"
                        aria-invalid={!!fieldErrors.fullName}
                        aria-describedby={fieldErrors.fullName ? "fullName-error" : undefined}
                      />
                      {fieldErrors.fullName && (
                        <p id="fullName-error" className="text-xs text-destructive mt-1">{fieldErrors.fullName}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        disabled={isSubmitting}
                        aria-required="true"
                        aria-invalid={!!fieldErrors.email}
                        aria-describedby={fieldErrors.email ? "email-error" : undefined}
                      />
                      {fieldErrors.email && (
                        <p id="email-error" className="text-xs text-destructive mt-1">{fieldErrors.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone (optional)</Label>
                      <Input
                        id="phone"
                        placeholder="+1 (555) 555-5555"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        disabled={isSubmitting}
                        aria-invalid={!!fieldErrors.phone}
                        aria-describedby={fieldErrors.phone ? "phone-error" : undefined}
                      />
                      {fieldErrors.phone && (
                        <p id="phone-error" className="text-xs text-destructive mt-1">{fieldErrors.phone}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="company">Company (optional)</Label>
                      <Input
                        id="company"
                        placeholder="Your company name"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        disabled={isSubmitting}
                        aria-invalid={!!fieldErrors.company}
                        aria-describedby={fieldErrors.company ? "company-error" : undefined}
                      />
                      {fieldErrors.company && (
                        <p id="company-error" className="text-xs text-destructive mt-1">{fieldErrors.company}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="website">Website (optional)</Label>
                    <Input
                      id="website"
                      placeholder="yourdomain.com"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      disabled={isSubmitting}
                      aria-invalid={!!fieldErrors.website}
                      aria-describedby={fieldErrors.website ? "website-error" : undefined}
                    />
                    {fieldErrors.website && (
                      <p id="website-error" className="text-xs text-destructive mt-1">{fieldErrors.website}</p>
                    )}
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="projectType">Project Type *</Label>
                      <Select
                        value={formData.projectType}
                        onValueChange={(value) => setFormData({ ...formData, projectType: value })}
                        disabled={isSubmitting}
                      >
                        <SelectTrigger
                          id="projectType"
                          aria-invalid={!!fieldErrors.projectType}
                          aria-describedby={fieldErrors.projectType ? "projectType-error" : undefined}
                        >
                          <SelectValue placeholder="Select a project type" />
                        </SelectTrigger>
                        <SelectContent>
                          {projectTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {fieldErrors.projectType && (
                        <p id="projectType-error" className="text-xs text-destructive mt-1">{fieldErrors.projectType}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="budget">Budget Range *</Label>
                      <Select
                        value={formData.budget}
                        onValueChange={(value) => setFormData({ ...formData, budget: value })}
                        disabled={isSubmitting}
                      >
                        <SelectTrigger
                          id="budget"
                          aria-invalid={!!fieldErrors.budget}
                          aria-describedby={fieldErrors.budget ? "budget-error" : undefined}
                        >
                          <SelectValue placeholder="Select a budget" />
                        </SelectTrigger>
                        <SelectContent>
                          {budgetRanges.map((range) => (
                            <SelectItem key={range} value={range}>
                              {range}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {fieldErrors.budget && (
                        <p id="budget-error" className="text-xs text-destructive mt-1">{fieldErrors.budget}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="timeline">Timeline *</Label>
                      <Select
                        value={formData.timeline}
                        onValueChange={(value) => setFormData({ ...formData, timeline: value })}
                        disabled={isSubmitting}
                      >
                        <SelectTrigger
                          id="timeline"
                          aria-invalid={!!fieldErrors.timeline}
                          aria-describedby={fieldErrors.timeline ? "timeline-error" : undefined}
                        >
                          <SelectValue placeholder="Select a timeline" />
                        </SelectTrigger>
                        <SelectContent>
                          {timelines.map((timeline) => (
                            <SelectItem key={timeline} value={timeline}>
                              {timeline}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {fieldErrors.timeline && (
                        <p id="timeline-error" className="text-xs text-destructive mt-1">{fieldErrors.timeline}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="pages">Approximate Pages (optional)</Label>
                    <Select
                      value={formData.pages}
                      onValueChange={(value) => setFormData({ ...formData, pages: value })}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger id="pages">
                        <SelectValue placeholder="Select a range" />
                      </SelectTrigger>
                      <SelectContent>
                        {pageRanges.map((range) => (
                          <SelectItem key={range} value={range}>
                            {range}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="mb-3 block">Features (optional)</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {features.map((feature) => (
                        <div key={feature.id} className="flex items-center gap-2">
                          <Checkbox
                            id={feature.id}
                            checked={formData.selectedFeatures.includes(feature.id)}
                            onCheckedChange={() => handleFeatureToggle(feature.id)}
                            disabled={isSubmitting}
                          />
                          <Label htmlFor={feature.id} className="text-sm font-normal cursor-pointer">
                            {feature.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="maintenance"
                      checked={formData.interestedInMaintenance}
                      onCheckedChange={(checked) =>
                        setFormData({ 
                          ...formData, 
                          interestedInMaintenance: checked as boolean,
                          maintenancePlan: checked ? formData.maintenancePlan : ""
                        })
                      }
                      disabled={isSubmitting}
                    />
                    <div>
                      <Label htmlFor="maintenance" className="text-sm font-normal cursor-pointer">
                        I'm interested in maintenance services
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Keep your site fast, secure, and up-to-date with our{" "}
                        <Link to="/services/maintenance" className="underline">all-inclusive maintenance plans</Link>
                      </p>
                    </div>
                  </div>

                  {formData.interestedInMaintenance && (
                    <div>
                      <Label htmlFor="maintenancePlan">Which maintenance plan?</Label>
                      <Select
                        value={formData.maintenancePlan}
                        onValueChange={(value) => setFormData({ ...formData, maintenancePlan: value })}
                        disabled={isSubmitting}
                      >
                        <SelectTrigger id="maintenancePlan">
                          <SelectValue placeholder="Not sure yet" />
                        </SelectTrigger>
                        <SelectContent>
                          {maintenancePlans.map((plan) => (
                            <SelectItem key={plan.id} value={plan.id}>
                              {plan.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Link 
                        to="/services/maintenance" 
                        className="text-xs text-muted-foreground underline mt-2 inline-block"
                      >
                        View detailed plan comparison
                      </Link>
                    </div>
                  )}

                  {/* Honeypot field - hidden from users, visible to bots */}
                  <div className="absolute opacity-0 pointer-events-none h-0 overflow-hidden" aria-hidden="true">
                    <Label htmlFor="website_url_confirm">Website URL Confirm</Label>
                    <Input
                      id="website_url_confirm"
                      name="website_url_confirm"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.honeypot}
                      onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="projectDetails">Project Details *</Label>
                    <Textarea
                      id="projectDetails"
                      placeholder="Tell us about your goals, audience, references, and anything else we should know..."
                      rows={5}
                      value={formData.projectDetails}
                      onChange={(e) => setFormData({ ...formData, projectDetails: e.target.value })}
                      disabled={isSubmitting}
                      aria-invalid={!!fieldErrors.projectDetails}
                      aria-describedby={fieldErrors.projectDetails ? "projectDetails-error" : undefined}
                    />
                    {fieldErrors.projectDetails && (
                      <p id="projectDetails-error" className="text-xs text-destructive mt-1">{fieldErrors.projectDetails}</p>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground">
                    We'll only use your information to respond to this request.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    By submitting this form, you agree to our{" "}
                    <a href="/privacy" className="underline">Privacy Policy</a> and{" "}
                    <a href="/terms" className="underline">Terms of Service</a>.
                  </p>

                  <Button type="submit" size="lg" disabled={isSubmitting} aria-busy={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        SUBMITTING...
                      </>
                    ) : (
                      "REQUEST QUOTE"
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Quote;
